import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, filtrosAParams, paramsAFiltros, type Filtros as TFiltros, type ResultadoBusqueda } from './api';
import { Encabezado, Portada } from './components/Encabezado';
import { Ficha } from './components/Ficha';
import { Filtros } from './components/Filtros';
import { Pie } from './components/Pie';
import { Resultados } from './components/Resultados';
import { regexResaltado } from './formato';
import { conParams, useRuta } from './ruta';

export function App() {
  const { ruta, navegar } = useRuta();
  const hcd = ruta.path === '/hcd' || ruta.path.startsWith('/hcd/');
  const base = hcd ? '/hcd' : '/';

  // La sección «Boletines mensuales» se quitó: su dirección vieja lleva al buscador.
  const esBoletines = ruta.path.endsWith('/boletines');
  useEffect(() => {
    if (esBoletines) navegar(base, { reemplazar: true });
  }, [esBoletines, base, navegar]);

  return (
    <>
      <a className="saltar" href="#contenido">
        Ir al contenido
      </a>
      <Encabezado base={base} hcd={hcd} navegar={navegar} />
      <Portada
        titulo="Boletín Oficial"
        bajada={
          hcd
            ? 'Normas del Honorable Concejo Deliberante y del Departamento Ejecutivo de Luján.'
            : 'Decretos, ordenanzas y resoluciones de la Municipalidad de Luján.'
        }
      />
      <Buscador base={base} params={ruta.params} navegar={navegar} />
      <Pie />
    </>
  );
}

interface BuscadorProps {
  base: string;
  params: URLSearchParams;
  navegar: (url: string, o?: { reemplazar?: boolean; arriba?: boolean }) => void;
}

function Buscador({ base, params, navegar }: BuscadorProps) {
  const clave = params.toString();
  const filtros = useMemo(() => paramsAFiltros(new URLSearchParams(clave)), [clave]);
  const docAbierto = Number(params.get('doc')) || null;

  // Sólo los filtros (sin ?doc) disparan una nueva búsqueda.
  const claveBusqueda = filtrosAParams(filtros).toString();

  const [anios, setAnios] = useState<number[]>([]);
  const [tipos, setTipos] = useState<string[]>([]);
  const [resultado, setResultado] = useState<ResultadoBusqueda | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  const abiertaDesdeLista = useRef(false);

  useEffect(() => {
    const ctl = new AbortController();
    api.anios(ctl.signal).then(setAnios).catch(() => {});
    api.tipos(ctl.signal).then(setTipos).catch(() => {});
    return () => ctl.abort();
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    setCargando(true);
    setError(null);
    api
      .buscar(filtros, ctl.signal)
      .then((r) => {
        setResultado(r);
        setCargando(false);
      })
      .catch((e: Error) => {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setCargando(false);
      });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveBusqueda, intento]);

  useEffect(() => {
    if (!docAbierto) document.title = 'Boletín Oficial · Municipalidad de Luján';
  }, [docAbierto]);

  const re = useMemo(() => regexResaltado(filtros.q, filtros.exacto), [filtros.q, filtros.exacto]);

  const aplicar = useCallback(
    (f: TFiltros) => navegar(conParams(base, filtrosAParams(f))),
    [base, navegar],
  );

  const abrir = (nro: number) => {
    const p = filtrosAParams(filtros);
    p.set('doc', String(nro));
    abiertaDesdeLista.current = true;
    navegar(conParams(base, p));
  };

  const cerrar = () => {
    if (abiertaDesdeLista.current) {
      abiertaDesdeLista.current = false;
      window.history.back();
    } else {
      navegar(conParams(base, filtrosAParams(filtros)), { reemplazar: true });
    }
  };

  return (
    <main className="pagina pagina--buscar" id="contenido">
      <div className="buscar">
        <aside className="buscar__filtros" aria-label="Filtros de búsqueda">
          <Filtros valor={filtros} anios={anios} tipos={tipos} onAplicar={aplicar} />
        </aside>
        <Resultados
          filtros={filtros}
          resultado={resultado}
          cargando={cargando}
          error={error}
          re={re}
          onAbrir={(d) => abrir(d.nroInterno)}
          onPagina={(p) => navegar(conParams(base, filtrosAParams({ ...filtros, pagina: p })), { arriba: true })}
          onReintentar={() => setIntento((n) => n + 1)}
        />
      </div>
      {docAbierto && <Ficha key={docAbierto} nro={docAbierto} re={re} onCerrar={cerrar} />}
    </main>
  );
}
